import { styled } from '@mui/material';
import { Grid, Typography } from '@kc-console/common-ui';

import { ScrollObserverEntryWrapper } from '../ScrollObserverWrapper';

import type { ReactNode } from 'react';
import type { DetailViewSectionProps } from './types';

const isTextAdornment = (value: ReactNode): value is number | string => {
  return typeof value === 'number' || typeof value === 'string';
};

const isRenderableAdornment = (value: ReactNode) => {
  return value != null && value !== false;
};

/**
 * [ DetailView의 props.sections 배열의 각 아이템을 랜더링하는 컴포넌트 ]
 *
 * - DetailView에서 주어진 sections 배열의 각 아이템을 랜더링하는 컴포넌트입니다.
 * - 서비스 개발 시 직접 사용되지 않으며 DetailView 내부에서 사용됩니다.
 * - 주어진 entryId로 ScrollObserver 엔트리로서 동작합니다.
 */
const DetailViewSection = ({
  header,
  headerEndAdornment,
  description,
  content,
  entryId,
  headerAction,
}: DetailViewSectionProps) => {
  const hasHeaderEndAdornment = isRenderableAdornment(headerEndAdornment);
  return (
    <ScrollObserverEntryWrapper
      entryId={entryId}
      targetComponent={
        <StyledWrapper>
          <StyledHeaderWrapper>
            <Grid container alignItems={'center'} justifyContent={'space-between'}>
              {hasHeaderEndAdornment ? (
                <StyledHeaderTitleGroup>
                  <StyledHeader component={'label'} variant={'label/semibold/xl'}>
                    {header}
                  </StyledHeader>
                  {isTextAdornment(headerEndAdornment) ? (
                    <StyledHeaderEndAdornment
                      variant={'label/semibold/xl'}
                      color={'text/weakest/default'}
                    >
                      {headerEndAdornment}
                    </StyledHeaderEndAdornment>
                  ) : (
                    headerEndAdornment
                  )}
                </StyledHeaderTitleGroup>
              ) : (
                <StyledHeader component={'label'} variant={'label/semibold/xl'}>
                  {header}
                </StyledHeader>
              )}
              {headerAction && <StyledHeaderAction>{headerAction}</StyledHeaderAction>}
            </Grid>
            {description && (
              <StyledDescription variant={'label/regular/sm'} color={'text/weaker/default'}>
                {description}
              </StyledDescription>
            )}
          </StyledHeaderWrapper>
          <StyledContent>{content}</StyledContent>
        </StyledWrapper>
      }
    />
  );
};

const StyledWrapper = styled('section')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledHeaderWrapper = styled('h2')`
  display: flex;
  flex-direction: column;
  row-gap: 2px;
`;

const StyledHeaderTitleGroup = styled('div')`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const StyledHeader = styled(Typography)`
  display: flex;
`;

const StyledHeaderEndAdornment = styled(Typography)`
  display: inline-flex;
  align-items: center;
`;

const StyledDescription = styled(Typography)`
  display: flex;
`;

const StyledHeaderAction = styled('div')``;

const StyledContent = styled('div')``;

export default DetailViewSection;
